/* eslint-disable camelcase */
import {
  useState,
  useEffect,
  useRef,
} from 'react';
import { format } from 'date-fns';
import { useStreamData } from '@lib/hooks/useStreamData';
import {
  postIpaUserEvent,
  wakeUpLambdaFunctions,
  connectToYoutubeStream,
  getLinkQuestions,
  stopEngagement
} from '@services/youtube-platform.service';
import ZoomSandbox from '@components/Sandbox/Zoom';
import { useRouter } from 'next/router';
import IpaSandbox from '@components/Sandbox/Ipa';
import MeetSandbox from '@components/Sandbox/Meet';
import TwitchSandbox from '@components/Sandbox/Twitch';
import YoutubeSandbox from '@components/Sandbox/Youtube';
import { pushDataLayerForEvent } from '@lib/gtag';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import useDeviceOrientation from '@lib/hooks/useDeviceOrientation';
import RequestLandscapeModal from '@components/Sandbox/RequestLandscapeModal';
import NoticeBubble from '@atoms/NoticeBubble';
import TeamsSandbox from './Teams';
import { useDisclosure } from '@heroui/react';
import { SignUpModal } from './SignupModal';
import Toast from '@atoms/Toast';
import Button from '@atoms/new/button';
import { removeAuthFromDomain } from '@services/identity.service';
import { nonInteractiveFeatures } from '@components/Layout/helper';
import { PlatformsTabs } from './PlatformTabGroup';
import KickSandbox from './kick';
import LinkedInSandbox from './linkedin';
import RestreamSandbox from './Restream';
import StreamyardSandbox from './Streamyard';
import AirmeetSandbox from './Airmeet';
import { useGeneratedResponses } from './useGeneratedResponses';
import { XIcon as CloseRoundedIcon } from '@phosphor-icons/react';

export default function SandboxWebsitePreview({
  platformId = '',
  platform = 'zoom',
  is_ros_preview = false,
  templateId = '54e37f08-2ae7-4a32-ac8c-ba60fe5d5d0b'
}) {
  const router = useRouter();
  const isEmbed = router.query.isEmbed === 'true';
  const { isMobile, orientation } = useDeviceOrientation();

  const [linkQuestions, setLinkQuestions] = useState(null);
  const [currentRosFeature, setCurrentRosFeature] = useState(null);
  const [next] = useState(linkQuestions?.length === 0);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [currentFeatureId] = useState(null);

  const [crudLoading, setCrudLoading] = useState(true);
  const teamContext = useTeamsData((s) => s.teamContext);
  const { setIntervalId } = useStreamData(s => ({ setIntervalId: s.setIntervalId }));
  const [currentRosResult] = useState([]);
  const [stream, setStream] = useState(null);
  const [notificationVisible, setNotificationVisible] = useState(isMobile);
  const [showSignUp, setShowSignUp] = useState(true);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useGeneratedResponses({
    feature: currentRosFeature,
    streamId: stream?.id,
    streamTitle: stream?.title,
  });

  const featurePrevState = () => {
    if (currentIndex > 0) {
      pushDataLayerForEvent('sandbox_previous_arrow_pressed', { streamId: stream.id }, false);

      setCurrentIndex(currentIndex - 1);
    }
  };

  const featureNextState = () => {
    if (currentIndex < linkQuestions.length - 1) {
      pushDataLayerForEvent('sandbox_next_arrow_pressed', { streamId: stream.id }, false);

      setCurrentIndex(currentIndex + 1);
    }
  };

  const stopFeature = async (id, type) => {
    if (nonInteractiveFeatures.includes(type)) {
      return;
    }

    const sessionId = localStorage.getItem('sessionId');
    pushDataLayerForEvent(`sandbox_stop_${type}_interaction`, { streamId: stream.id }, false);

    stopEngagement(
      stream.id,
      type,
      {
        sessionId,
        engagementId: id,
        platformType: stream.platform_type
      }
    );
  };

  useEffect(() => {
    if (!currentIndex && !linkQuestions) return;
    stopFeature(currentFeatureId, currentRosFeature?.type);
    setCurrentRosFeature(linkQuestions?.[currentIndex || 0]);
    pushDataLayerForEvent(`sandbox_start_${currentRosFeature?.type}_interaction`, { streamId: stream.id }, false);
  }, [currentIndex, linkQuestions]);

  const connectToStream = async (event) => {
    await wakeUpLambdaFunctions();
    const newIntervalId = setInterval(async () => {
      await wakeUpLambdaFunctions();
    }, 15 * 60 * 1000);
    setIntervalId(newIntervalId);
    const scheduledStartTime = event?.event_time;
    const engageType = new Date().getTime() > new Date(scheduledStartTime).getTime()
      ? 'active'
      : 'scheduled';

    const res = await connectToYoutubeStream({
      liveChatId: event.id,
      platformId: event.platform_id,
      statusFlag: true,
      engageType,
      platformType: event.livestream.platform_type,
      entityId: event.id,
      title: event.title,
      userId: event.livestream.user_id,
      orgStartTime: event.event_time,
      totalTimeWithText: '12 PM'
    });

    setStream(res.entity);
    setCurrentIndex(res.entity.ros_index || 0);

    const id = res.entity.template_id;
    const tempQuestions = await getLinkQuestions(id);
    setLinkQuestions(tempQuestions.entity);

    setCurrentRosFeature(tempQuestions.entity[res.entity.ros_index || 0]);

    localStorage.setItem('streamId', res.entity.id);
    localStorage.setItem('sessionId', res.entity.sessionId);
    window.parent.postMessage('ready', '*');
  };

  const connectSandbox = async () => {
    setCrudLoading(true);
    const date = new Date();
    const event_time = date.toISOString();

    const res = await postIpaUserEvent({
      platform_id: platformId,
      title: 'sandbox',
      event_time,
      user_id: teamContext?.oid,
      template_id: templateId,
      event_type: 'ipa',
      is_sandbox: true,
      is_ai_generated: true,
      is_ros_preview,
      totalTimeWithText: format(new Date(event_time), 'hh:mm a')
    });

    connectToStream(res.entity);

    setCrudLoading(false);
  };

  useEffect(() => {
    if (!teamContext?.oid) return;

    connectSandbox();
  }, [teamContext]);

  const handleKeyStrokes = (e) => {
    const { key } = e;

    if (key?.match(/ArrowRight/)) {
      e.preventDefault();
      featureNextState();
    }

    if (key?.match(/ArrowLeft/)) {
      e.preventDefault();
      featurePrevState();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyStrokes);
    return () => {
      window.removeEventListener('keydown', handleKeyStrokes);
    };
  }, [currentIndex, currentRosFeature]);

  const [showOrientationModal, setShowOrientationModal] = useState(false);

  useEffect(() => {
    setNotificationVisible(isMobile);

    if (isMobile && orientation === 'Portrait') {
      setShowOrientationModal(true);
      return;
    }

    setShowOrientationModal(false);
  }, [isMobile, orientation]);

  const {
    isOpen: isOpenSignup,
    onOpen: onOpenSignup,
    onOpenChange: onOpenSignupChange,
    onClose: onCloseSignup
  } = useDisclosure();

  useEffect(() => {
    window.removeEventListener('keydown', handleKeyStrokes);
  }, [isOpenSignup]);

  useEffect(() => {
    if (!linkQuestions || linkQuestions.length <= 0) return;
    const signUpSlideIndex = linkQuestions.findIndex((lq) => lq.title === 'Blank-Signup');

    if (currentIndex === signUpSlideIndex) {
      onOpenSignup();
    }
  }, [currentIndex, linkQuestions]);

  const getPlatformUI = () => {
    if (platform === 'kick') {
      return <KickSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'linkedin') {
      return <LinkedInSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'restream') {
      return <RestreamSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'streamyard') {
      return <StreamyardSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'airmeet') {
      return <AirmeetSandbox
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'teams') {
      return <TeamsSandbox
        isAiSandbox={true}
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }
    if (platform === 'ipa') {
      return <IpaSandbox
        isAiSandbox={true}
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'meet') {
      return <MeetSandbox
        isAiSandbox={true}
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'twitch') {
      return <TwitchSandbox
        isAiSandbox={true}
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    if (platform === 'youtube') {
      return <YoutubeSandbox
        isAiSandbox={true}
        stream={stream}
        currentIndex={currentIndex}
        featurePrevState={featurePrevState}
        featureNextState={featureNextState}
        linkQuestions={linkQuestions || []}
        nextRef={nextRef}
        prevRef={prevRef}
        next={next}
        feature={currentRosFeature}
        stopFeature={stopFeature}
        currentRosResult={currentRosResult}
      />;
    }

    return <ZoomSandbox
      isAiSandbox={true}
      stream={stream}
      currentIndex={currentIndex}
      featurePrevState={featurePrevState}
      featureNextState={featureNextState}
      linkQuestions={linkQuestions || []}
      nextRef={nextRef}
      prevRef={prevRef}
      next={next}
      feature={currentRosFeature}
      stopFeature={stopFeature}
      currentRosResult={currentRosResult}
    />;
  };

  const setPlatform = (newPlatform) => {
    pushDataLayerForEvent('sandbox_platform_changed', { platform: newPlatform });
    const [pathWithoutQueryParams] = router.asPath.split('?');
    const queryParams = router.query;
    delete queryParams.platform;
    const newQueryParams = new URLSearchParams({
      ...queryParams,
      platform: newPlatform
    }).toString();
    router.replace(`${pathWithoutQueryParams}?${newQueryParams}`);
  };

  if (crudLoading) return null;

  const streamTitle = stream?.title.replace(/-\d+$/, '');

  return (
    <div className='relative'>
      <div className='flex items-center justify-between bg-white z-50 py-3 px-4 mb-4'>
        <p className='font-semibold md:text-xl text-sm w-1/4'>{streamTitle}</p>

        <div className='w-1/2 flex justify-center'>
          <PlatformsTabs
            platform={platform}
            selectPlatform={setPlatform}
          />
        </div>

        <div className='w-1/4 flex gap-2 justify-end items-center'>
          <Button
            onPress={() => {
              window.open(`${process.env.NEXT_PUBLIC_REGISTER_ROUTE}?duplicateTemplateId=${templateId}`, '_blank');
            }}
          >
            Duplicate Template
          </Button>
          {!isEmbed && (
            <Button
              onPress={() => {
                removeAuthFromDomain();
                router.back();
              }}
              kind='tertiary'
              endContent={<CloseRoundedIcon size={20} />}
            >
              Close
            </Button>
          )}
        </div>
      </div>
      <div>
        <div
          className='px-4 max-w-[1280px] min-w-[480px] mx-auto'>
          {getPlatformUI()}
        </div>
        <p className='text-center text-grey-700 text-sm font-normal mt-6'>This live presentation lets you interact with both screens to see how it works for the presenter and your audience in real-time.</p>
      </div>

      {currentIndex === linkQuestions?.length - 1 && showSignUp && <div className='absolute bottom-0 left-5 z-50'>
        <Toast
          showIcon={false}
          onClose={() => setShowSignUp(false)}
          headerClassName='font-normal'
          header="The free plan includes all interactions for a smaller audience."
        >
          <Button
            kind='secondary'
            size='sm'
            className='w-[115px]'
            onPress={() => window.open(process.env.NEXT_PUBLIC_REGISTER_ROUTE, '_blank')}
          >
            Sign up now
          </Button>
        </Toast>
      </div>}

      {isOpenSignup && (
        <SignUpModal
          isOpen={isOpenSignup}
          onOpenChange={onOpenSignupChange}
          onClose={() => {
            const signUpSlideIndex = linkQuestions.findIndex((lq) => lq.title === 'Blank-Signup');
            if (signUpSlideIndex === -1) return;

            setLinkQuestions(prev => prev.filter((_, index) => index !== signUpSlideIndex));
            onCloseSignup();
          }}
        />
      )}

      <RequestLandscapeModal visible={showOrientationModal} />
      <NoticeBubble
        visible={notificationVisible}
        onClose={() => setNotificationVisible(false)}
        message="This demo is best seen in a larger screen."
      />
    </div>
  );
}
